import React, { useState } from 'react';
import { Modal, Table, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCustomerOperationLogs } from '../graphql/queries/customerManagement';
import { CustomerOperationLogType } from '../graphql/types/CustomerManagementTypes';
import dayjs from 'dayjs';

interface CustomerLogModalProps {
    visible: boolean;
    onClose: () => void;
    channelId: number;
}

/**
 * PRD 日志查询分为三个 Tab：
 * - 账户增删 → operationType: null（查全部后前端过滤 CREATE/DELETE）
 * - 账号发送 → operationType: SEND_EMAIL
 * - 密码重置 → operationType: RESET_PASSWORD
 *
 * 因为后端 queryLogs 只支持单一 operationType，Tab1 使用 null 查全部再前端过滤。
 */
interface TabConfig {
    key: string;
    operationType: string | null;
    clientFilter?: string[];
}

const TAB_CONFIGS: TabConfig[] = [
    { key: 'accountChanges', operationType: null, clientFilter: ['CREATE', 'DELETE'] },
    { key: 'credentialsSent', operationType: 'SEND_EMAIL' },
    { key: 'passwordReset', operationType: 'RESET_PASSWORD' },
];

const PAGE_SIZE = 10;

const CustomerLogModal: React.FC<CustomerLogModalProps> = ({ visible, onClose, channelId }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('accountChanges');
    const [page, setPage] = useState(1);

    const currentConfig = TAB_CONFIGS.find((c) => c.key === activeTab) || TAB_CONFIGS[0];

    const { data, loading } = useCustomerOperationLogs(
        visible ? channelId : null,
        currentConfig.operationType,
        currentConfig.clientFilter ? 1 : page,
        currentConfig.clientFilter ? 999 : PAGE_SIZE
    );

    const logPage = data?.customerOperationLogs;
    let logs = logPage?.content || [];
    let total = logPage?.totalElements || 0;

    // Tab1 需要前端过滤
    if (currentConfig.clientFilter) {
        logs = logs.filter((l) => currentConfig.clientFilter!.includes(l.operationType));
        total = logs.length;
        logs = logs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    }

    const parseDetailObject = (detailJson: string | undefined | null): Record<string, unknown> | null => {
        if (!detailJson) return null;
        try {
            const obj = JSON.parse(detailJson);
            return obj && typeof obj === 'object' ? obj : null;
        } catch {
            return null;
        }
    };

    // roleCode 单独出「类型」列，不再混在「其他」里
    const parseDetail = (detailJson: string | undefined | null): string => {
        if (!detailJson) return '-';
        const obj = parseDetailObject(detailJson);
        if (!obj) return detailJson;
        const rest = Object.entries(obj).filter(([k]) => k !== 'roleCode');
        return rest.length ? rest.map(([k, v]) => `${k}: ${v}`).join(', ') : '-';
    };

    /** 开户 / 销户类型：来自 detail_json.roleCode；历史日志无该字段显示 "-" */
    const roleLabel = (detailJson: string | undefined | null): string => {
        const roleCode = parseDetailObject(detailJson)?.roleCode;
        if (roleCode === 'CUSTOMER_ADMIN') return t('central.channels.crm.accountModal.roleAdmin');
        if (roleCode === 'CUSTOMER_VIEWER') return t('central.channels.crm.accountModal.roleOperator');
        return '-';
    };

    const operationLabel = (type: string): string => {
        const map: Record<string, string> = {
            CREATE: t('central.channels.crm.logModal.opCreate'),
            DELETE: t('central.channels.crm.logModal.opDelete'),
            RESET_PASSWORD: t('central.channels.crm.logModal.opResetPassword'),
            SEND_EMAIL: t('central.channels.crm.logModal.opSendEmail'),
            LOGIN: t('central.channels.crm.logModal.opLogin'),
            LOGIN_FAIL: t('central.channels.crm.logModal.opLoginFail'),
            CHANGE_PASSWORD: t('central.channels.crm.logModal.opChangePassword'),
        };
        return map[type] || type;
    };

    const columns = [
        {
            title: t('central.channels.crm.logModal.date'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (val: string) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-'),
        },
        {
            title: t('central.channels.crm.logModal.operator'),
            dataIndex: 'operatorName',
            key: 'operatorName',
            width: 120,
        },
        {
            title: t('central.channels.crm.logModal.operation'),
            dataIndex: 'operationType',
            key: 'operationType',
            width: 120,
            render: (val: string) => operationLabel(val),
        },
        {
            title: t('central.channels.crm.logModal.type'),
            dataIndex: 'detailJson',
            key: 'roleType',
            width: 120,
            render: (val: string) => roleLabel(val),
        },
        {
            title: t('central.channels.crm.logModal.details'),
            dataIndex: 'detailJson',
            key: 'detailJson',
            render: (val: string) => parseDetail(val),
        },
    ];

    const handleTabChange = (key: string) => {
        setActiveTab(key);
        setPage(1);
    };

    const tabItems = TAB_CONFIGS.map((config) => ({
        key: config.key,
        label: t(`central.channels.crm.logModal.${config.key}`),
    }));

    return (
        <Modal
            title={
                <div className="flex items-center">
                    <div className="w-1 h-6 bg-violet-600 mr-2" />
                    <span>{t('central.channels.crm.logModal.title')}</span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={900}
            destroyOnClose
        >
            <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />
            <Table
                dataSource={logs}
                columns={columns}
                rowKey="id"
                loading={loading}
                size="small"
                pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total,
                    onChange: (p) => setPage(p),
                    showSizeChanger: false,
                }}
            />
        </Modal>
    );
};

export default CustomerLogModal;
